import React, { useRef, useEffect, useState, useContext } from 'react'
import { get } from 'lodash'
import { ReactReduxContext, useSelector, useDispatch } from 'react-redux'
import { useLocation, useHistory, Redirect } from 'react-router-dom'
import { Spinner, Button, Form } from 'react-bootstrap'
import Helmet from 'react-helmet'
import isEqual from 'lodash.isequal'
import { isClient, extractOriginalContractNumber } from 'helpers/tools'
import CustomPages from 'components/templates/CustomPages'
import CustomModal from 'components/molecules/CustomModal'
import ErrorPages from 'components/templates/ErrorPages'
import { getData } from 'store/modules/customPage/actions'
import apiClient from 'api/config/apiClient'
import { getMenuName } from 'helpers/auth'
import Typography from 'components/atoms/Typography'

const useScrollTo = (hash, elem) => {
  setTimeout(() => {
    if (!!hash) {
      elem?.scrollIntoView()
    } else {
      try {
        window.scrollTo({ top: 0, left: 0, behavior: 'smooth' })
      }
      catch (error) {
        elem?.scrollIntoView()
      }
    }
  }, 0)
}

const CustomPage = ({ user, refs }) => {
  const { store } = useContext(ReactReduxContext)
  const dispatch = useDispatch()
  const location = useLocation()
  const history = useHistory()
  const pageRef = useRef()
  const [firstLoad, setFirstLoad] = useState(true)
  const [showEquipmentModal, setShowEquipmentModal] = useState(false)
  const [equipmentModalStep, setEquipmentModalStep] = useState('question')
  const [equipmentConfirmNotUse, setEquipmentConfirmNotUse] = useState(false)
  const [equipmentError, setEquipmentError] = useState('')
  const [needsRegister, setNeedsRegister] = useState(null)
  const require_login = location.pathname.indexOf('/app') === 0 ? true : false
  const { data, dataError, loading } = useSelector(({ customPage }) => customPage, isEqual)
  const session = useSelector((state) => state?.auth?.user)
  const isLogged = Boolean(user || user?.setUserProfile)
  const menu_name = getMenuName(user)

  let metaTags = data?.route?.entity?.metaTags
  let metaTitle = ''

  const handleEquipmentYes = () => {
    setShowEquipmentModal(false)
    setEquipmentModalStep('question')
    setEquipmentConfirmNotUse(false)
    history.push('/app/equipamento-medico/cadastrar')
  }
  const handleEquipmentNo = () => setEquipmentModalStep('justify')
  const handleEquipmentBack = () => {
    setEquipmentModalStep('question')
    setEquipmentConfirmNotUse(false)
    setEquipmentError('')
  }

  const handleEquipmentSubmit = () => {
    setEquipmentError('')
    let contract = user?.contract || ''
    if (!contract && session?.contract) {
      contract = session.contract
    }
    const provider = extractOriginalContractNumber(contract)
    if (provider) {
      apiClient
        .request('cache-disable/equipment_register_no_equipment', { provider: String(provider), store })
        .then((res) => {
          const result = res?.applications?.medicalEquipment?.registerNoEquipment
          if (result?.registered === false) {
            setEquipmentError('Não foi possível concluir o envio. Tente novamente mais tarde.')
          } else {
            setShowEquipmentModal(false)
            setEquipmentModalStep('question')
            setEquipmentConfirmNotUse(false)
          }
        })
        .catch(() => {
          setEquipmentError('Não foi possível concluir o envio. Tente novamente mais tarde.')
        })
    } else {
      setShowEquipmentModal(false)
      setEquipmentModalStep('question')
      setEquipmentConfirmNotUse(false)
    }
  }

  useEffect(() => {
    useScrollTo(location.hash, pageRef.current)
    if (require_login && !isLogged) {
      history.push(`/login?path=${location.pathname}`)
    } else if ((require_login && isLogged) && (user || get(user, 'setUserProfile'))) {
      const profileActive = get(user, 'active')
      const profileActiveBenner = get(user, 'active_benner')
      const profileId = get(user, 'profile_id')
      if (!profileId || !profileActive || !profileActiveBenner) {
        require_login && history.push('/change-profile')
      }
    } else {
      getMeta()
    }
  }, [location])

  useEffect(() => {
    const isCooperado = user?.menu === 'cooperado' || user?.menu?.includes('cooperado')
    if (!isCooperado) return

    const timeoutId = setTimeout(() => {
      let contract = user?.contract || ''
      if (!contract && session?.contract) {
        contract = session.contract
      }
      const provider = extractOriginalContractNumber(contract)
      if (provider) {
        const allowedPaths = ['/app', '/app/home-cooperado', '/app/home-prestador']
        const currentPath = window.location.pathname.replace(/\/+$/, '')
        const isAllowedPath = allowedPaths.includes(currentPath)
        if (isAllowedPath) {
          apiClient
            .request('cache-disable/equipment_needs_register', { provider: String(provider), store })
            .then((res) => {
              const needsRegisterResult = res?.applications?.medicalEquipment?.needsRegister
              const flag = needsRegisterResult?.needs_register
              setNeedsRegister(Boolean(flag))
            })
            .catch(() => {})
        }
      }
    }, 1250)
    return () => clearTimeout(timeoutId)
  }, [])

  useEffect(() => {
    if (user || get(user, 'setUserProfile')) {
      const profileActive = get(user, 'active')
      const profileActiveBenner = get(user, 'active_benner')
      const profileId = get(user, 'profile_id')
      if (!profileId || !profileActive || !profileActiveBenner) {
        require_login && history.push('/change-profile')
      } else {
        if (!firstLoad) {
          const payload = {
            pathname: location.pathname,
            search: location.search,
            menu_name: menu_name,
            store: store,
          }
          dispatch(getData(payload))
          getMeta()
        }
      }
    }
    setFirstLoad(false)
  }, [user])

  const getMeta = () => {
    let metaOgTitle = ''
    let metaDescription = ''
    let metaOgDescription = ''
    let twitterCreator = ''
    let twitterTitle = ''
    let twitterDescription = ''
    let meta = []

    metaTags?.map((item) => {
      if (item.key === 'title') metaTitle = item?.value
      if (item.key === 'description') metaDescription = item?.value
    })

    return [
      {
        name: `description`,
        content: metaDescription,
      },
      {
        property: `og:title`,
        content: metaOgTitle,
      },
      {
        property: `og:description`,
        content: metaOgDescription,
      },
      {
        property: `og:type`,
        content: `website`,
      },
      {
        name: `twitter:card`,
        content: `summary`,
      },
      {
        name: `twitter:creator`,
        content: twitterCreator,
      },
      {
        name: `twitter:title`,
        content: twitterTitle,
      },
      {
        name: `twitter:description`,
        content: twitterDescription,
      },
    ].concat(meta)
  }

  let parsedMeta = getMeta()
  useEffect(() => {
    if (needsRegister === true) {
      setShowEquipmentModal(true)
    }
  }, [needsRegister])
  if (dataError) return <ErrorPages errors={dataError} user={user} refs={refs} />
  if (data?.route) {
    const { redirect_url, status_code } = data?.route
    if (!user && status_code === '403') {
      return <Redirect to={`/login?path=${location.pathname}`} />
    }
    if (status_code?.match(/40[0-9]/)) {
      // Redirect caso a Home de algum perfil não exista no Drupal será redirecionado para home padrão
      if (location.pathname.match(/app\/home-.+/g)) {
        return <Redirect to="/app" />
      }
      return <ErrorPages status={status_code} errors={dataError} user={user} refs={refs} />
    }
    if (status_code?.match(/30[0-9]/)) {
      return <Redirect to={redirect_url} />
    }
    return (
      <div ref={pageRef}>
        {isClient() && loading && (
          <div className="loading-page">
            <Spinner className="loading-page__spinner" size="lg" animation="border" variant="white" />
          </div>
        )}
        <Helmet defaultTitle={metaTitle} meta={parsedMeta} />
        <CustomPages data={!loading ? data : null} user={user} refs={refs} />
        <div className="custom-page--medical-equipment-modal">
          <CustomModal
            open={showEquipmentModal}
            closeModal={() => setShowEquipmentModal(false)}
            size="sm"
            title="Equipamentos UNIMED"
          >
            {equipmentModalStep === 'question' && (
              <div className="text-center">
                <Typography variant="p" color="gray-100">
                  Você utiliza algum equipamento médico durante o atendimento aos beneficiários da Unimed?
                </Typography>
                <div className="d-flex justify-content-center mt-3">
                  <Button variant="primary" className="mx-1" onClick={handleEquipmentYes}>
                    Sim
                  </Button>
                  <Button variant="primary" className="mx-1" onClick={handleEquipmentNo}>
                    Não
                  </Button>
                </div>
              </div>
            )}
            {equipmentModalStep === 'justify' && (
              <div>
                {equipmentError ? (
                  <p className="text-danger text-center mt-2">{equipmentError}</p>
                ) : (
                  <>
                    <div className="mt-2 d-flex align-items-start" style={{ gap: '10px', padding: '0px 25px 0px' }}>
                      <input
                        type="checkbox"
                        id="equipmentConfirmNotUse"
                        checked={equipmentConfirmNotUse}
                        onChange={(e) => setEquipmentConfirmNotUse(e.target.checked)}
                        style={{ flexShrink: 0, width: '20px', height: '20px', cursor: 'pointer', marginTop: '2px' }}
                      />
                      <label htmlFor="equipmentConfirmNotUse" style={{ margin: 0, textAlign: 'left' }}>
                        <Typography variant="span" color="gray-100">Declaro que não faço uso de equipamentos médicos durante o atendimento aos beneficiários Unimed.</Typography>
                      </label>
                    </div>
                    <div className="d-flex justify-content-center mt-3" style={{ marginBottom: '1rem' }}>
                      <Button variant="secondary" className="mx-1" onClick={handleEquipmentBack}>
                        Voltar
                      </Button>
                      <Button
                        variant="primary"
                        className="mx-1"
                        onClick={handleEquipmentSubmit}
                        disabled={!equipmentConfirmNotUse}
                      >
                        Enviar
                      </Button>
                    </div>
                  </>
                )}
              </div>
            )}
          </CustomModal>
        </div>
      </div>
    )
  }
  return <ErrorPages status={404} user={user} refs={refs} />
}

export default CustomPage
